import {
  getWixThemeVariableToColorMap,
  getColorToWixThemeVariableMap,
  getFontToWixThemeVariableMap,
  getFontPresetToWixThemeVariableMap,
} from '@wix/wix-site-theme-variables';

const WST_VAR_REGEX = /^var\(--(wst.+)\)$/;
const WST_PREFIX = 'wst-';

const wixThemeVariableToColorMap = getWixThemeVariableToColorMap();
const colorToWixThemeVariableMap = getColorToWixThemeVariableMap();
const fontToWixThemeVariableMap = getFontToWixThemeVariableMap();
const wixThemeVariableToLegacyFontKeyMap = Object.fromEntries(
  Object.entries(fontToWixThemeVariableMap).map(([key, value]) => [value, key]),
);

export const extractWixGlobalVariable = (
  cssValue: string | undefined,
): string | null => {
  if (!cssValue) {
    return null;
  }
  const match = cssValue.match(WST_VAR_REGEX);
  return match?.[1] ?? null;
};

export const convertWixGlobalVariableToLegacyColorKey = (
  cssVariableName: string,
): string | null => {
  if (!cssVariableName.includes(WST_PREFIX)) {
    return null;
  }

  const legacyColorKeys =
    wixThemeVariableToColorMap[
      cssVariableName as keyof typeof wixThemeVariableToColorMap
    ];

  /**
   * This is intentional, because there are cases when
   * multiple legacy colors mapping to the same wix theme variable.
   * This limitation is known and accepted.
   *
   * js-sdk rewires colors under color_11:
   * https://github.com/wix-private/js-sdk/blob/b93f9812925c1fde64719b8e88bbb476a7f78dfd/packages/wix-sdk/js/modules/privates/templateUtils.js#L31
   *
   * It's best to avoid them
   */
  const lastLegacyColorKey = legacyColorKeys?.[legacyColorKeys.length - 1];

  return lastLegacyColorKey || null;
};

export const convertLegacyColorKeyToWixGlobalVariable = (
  legacyColor: string,
): string | null => {
  const variable = colorToWixThemeVariableMap[legacyColor];
  return variable || null;
};

export const convertWixThemeVariableToLegacyFontKey = (
  wixFontPreset: string,
): string | null => {
  const variable = wixThemeVariableToLegacyFontKeyMap[wixFontPreset];
  return variable || null;
};

const fontPresetToVariableMap = getFontPresetToWixThemeVariableMap();
const variableToFontPresetMap = Object.entries(fontPresetToVariableMap).reduce(
  (acc, [key, value]) => {
    if (!value) {
      return acc;
    }
    if (acc[value]) {
      acc[value].push(key);
    } else {
      acc[value] = [key];
    }
    return acc;
  },
  {} as Record<string, string[]>,
);

export const convertFontPresetToGlobalVariable = (preset: string) => {
  return fontPresetToVariableMap[preset] ?? null;
};

export const convertWixThemeVariableToLegacyFontPreset = (
  fontKey: string,
): string | null => {
  return variableToFontPresetMap[fontKey][0] ?? null;
};
